import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import {
  ColumnDef,
  OverlayService,
  SignalDataTableComponent,
  State,
  TableActionEvent,
  TableStateEvent,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { ConfirmOverlayComponent } from '@core/components/confirm-overlay/confirm-overlay';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { ContactResponse } from '@core/api/workspace/models/contactResponse';
import type { DeleteContactApiResponse } from '@core/api/workspace/models/deleteContactApiResponse';
import type { ListContactsApiResponse } from '@core/api/workspace/models/listContactsApiResponse';
import {
  CreateContactOverlayComponent,
  type CreateContactOverlayResult,
} from '../create-contact-overlay/create-contact-overlay';
import {
  EditContactOverlayComponent,
  type EditContactOverlayResult,
} from '../edit-contact-overlay/edit-contact-overlay';
import { ContactNameCellComponent } from '../contact-name-cell';
import { ContactStatusCellComponent } from '../contact-status-cell';
import { PeopleToolbarService } from '../people-toolbar.service';

type ContactRow = ContactResponse & { displayName: string };

@Component({
  selector: 'app-contacts-page',
  standalone: true,
  imports: [SignalDataTableComponent],
  templateUrl: './contacts-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'page-fill' },
})
export class ContactsPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);
  private readonly toolbar = inject(PeopleToolbarService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tableState = new State<ListContactsApiResponse>();
  readonly deleteState = new State<DeleteContactApiResponse>();

  private lastTableState: TableStateEvent = {
    searchText: '',
    paginationEvent: { pageNumber: 1, pageSize: 100 },
  };

  readonly columnDefs: ColumnDef[] = [
    {
      title: 'Name',
      type: 'custom',
      component: ContactNameCellComponent,
    },
    {
      title: 'Email',
      key: 'email',
      type: 'text',
    },
    {
      title: 'Status',
      type: 'custom',
      component: ContactStatusCellComponent,
    },
    {
      title: 'Updated',
      key: 'updatedAt',
      type: 'date',
    },
    {
      title: 'Actions',
      type: 'actions',
      actionsConfig: {
        iconActions: [
          {
            actionKey: 'edit',
            iconPath: 'assets/icons/square-edit-outline.svg',
            label: 'Edit',
          },
          {
            actionKey: 'delete',
            iconPath: 'assets/icons/delete-outline.svg',
            label: 'Delete',
          },
        ],
      },
    },
  ];

  ngOnInit(): void {
    this.toolbar.createClicks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onCreate();
      });
  }

  onTableStateChanged(event: TableStateEvent): void {
    this.lastTableState = event;
    this.loadItems(event);
  }

  onTableAction(event: TableActionEvent): void {
    const item = event.item as ContactRow;

    if (event.actionKey === 'edit') {
      this.onEdit(item);
      return;
    }

    if (event.actionKey === 'delete') {
      this.onDelete(item);
    }
  }

  onCreate(): void {
    const dialogRef = this.overlayService.openModal(
      CreateContactOverlayComponent,
    );

    dialogRef.closed.subscribe(
      (result: CreateContactOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadItems(this.lastTableState);
      },
    );
  }

  private onEdit(item: ContactResponse): void {
    const dialogRef = this.overlayService.openModal(
      EditContactOverlayComponent,
      {
        data: {
          id: item.id,
          firstName: item.firstName,
          lastName: item.lastName,
          email: item.email,
          status: item.status,
        },
      },
    );

    dialogRef.closed.subscribe(
      (result: EditContactOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadItems(this.lastTableState);
      },
    );
  }

  private onDelete(item: ContactResponse): void {
    const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
    const dialogRef = this.overlayService.openModal(ConfirmOverlayComponent, {
      data: {
        title: 'Delete contact',
        message: `Are you sure you want to delete “${name}”? They will also be removed from all groups.`,
        confirmLabel: 'Delete',
      },
      panelClass: ['confirm-overlay-panel'],
      fitContent: true,
    });

    dialogRef.closed.subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }

      this.executeRequest({
        state: this.deleteState,
        request: this.userAPIService.deleteContact(item.id),
        showLoader: true,
        handleError: true,
        onSuccess: () => {
          this.toasterService.success('Contact deleted.');
          this.loadItems(this.lastTableState);
        },
      });
    });
  }

  private loadItems(event: TableStateEvent): void {
    const search = event.searchText?.trim();
    const pageNumber = event.paginationEvent?.pageNumber ?? 1;
    const pageSize = event.paginationEvent?.pageSize ?? 100;

    this.executeRequest({
      state: this.tableState,
      request: this.userAPIService
        .listContacts({
          ...(search ? { search } : {}),
          pageNumber,
          pageSize,
        })
        .pipe(
          map((response) => ({
            ...response,
            data: {
              ...response.data,
              items: (response.data?.items ?? []).map((item) => ({
                ...item,
                displayName: [item.firstName, item.lastName]
                  .filter(Boolean)
                  .join(' '),
              })),
            },
          })),
        ),
      handleError: true,
    });
  }
}
